import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@city-of-helsinki/react-helsinki-headless-cms';
import { useMenuQuery } from '@city-of-helsinki/react-helsinki-headless-cms/apollo';
import { Logo, Footer as HDSFooter, logoSv, logoFi, LogoSize } from 'hds-react';

import styles from './footer.module.scss';
import { getCurrentLanguage } from '../../../common/translation/TranslationUtils';
import { RESET_FOCUS_ID } from '../../../common/components/resetFocus/constants';
import { languageToMenuNameMap } from './constants';

type ValidMenuItem = {
  id: MenuItem['id'];
  label: NonNullable<MenuItem['label']>;
  path: NonNullable<MenuItem['path']>;
};

const isValidMenuItem = (menuItem: MenuItem): menuItem is ValidMenuItem =>
  !!menuItem.label && !!menuItem.path;

const Footer: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const currentLocale = getCurrentLanguage(i18n);

  // override Footer component default behaviour which focuses skip-link
  const handleBackToTop = () => {
    globalThis.window?.scrollTo({ top: 0 });
    document.querySelector<HTMLDivElement>(`#${RESET_FOCUS_ID}`)?.focus();
  };

  const footerMenuQuery = useMenuQuery({
    variables: {
      id: languageToMenuNameMap[currentLocale],
      menuIdentifiersOnly: true,
    },
  });
  const footerLinks =
    footerMenuQuery.data?.menu?.menuItems?.nodes?.filter(isValidMenuItem) ?? [];

  return (
    <HDSFooter title={t('appName')} className={styles.footer}>
      <HDSFooter.Base
        copyrightHolder={t('common.cityOfHelsinki')}
        logo={
          <Logo
            src={currentLocale === 'sv' ? logoSv : logoFi}
            size={LogoSize.Medium}
            alt={t('common.cityOfHelsinki')}
          />
        }
        backToTopLabel={t('footer.backToTop')}
        onBackToTopClick={handleBackToTop}
      >
        {footerLinks.map((footerLink) => (
          <HDSFooter.Link
            key={footerLink.id}
            href={footerLink.path}
            label={footerLink.label}
          />
        ))}
      </HDSFooter.Base>
    </HDSFooter>
  );
};

export default Footer;
